import type {
  PosterLayout,
  PosterLogoSize,
  PosterThemeId,
} from '@/types'

// A theme is a coherent visual treatment — palette, type pairing, and a
// handful of optional decoration hints (grain, glows, organic shapes)
// that the PosterCanvas reads to layer on extra atmosphere. Each theme
// works for every (purpose × layout) combination; differences across
// layouts come from the LOGO_SIZES table below, not per-theme overrides.
export interface PosterTheme {
  id: PosterThemeId
  name: string
  /** One-line description shown in the theme picker. */
  tagline: string
  // ── Color ────────────────────────────────────────────────────────
  /** Full CSS `background:` value — string, gradient, etc. */
  bg: string
  /** Body text colour on the poster background. */
  ink: string
  /** Secondary text (subhead, footer, captions). */
  inkMuted: string
  /** Accent used for the headline emphasis and QR frame trim. */
  accent: string
  /** Surface used for the QR card itself. */
  surface: string
  /** Text colour on the QR card (where caption + decorations sit). */
  surfaceInk: string
  /** Hairline / divider colour. */
  border: string
  // ── Typography ───────────────────────────────────────────────────
  serifFont: string
  sansFont: string
  /** Tighter line-height for serif themes; falls back to default. */
  headlineLeading: number
  // ── Decoration toggles read by the renderer ──────────────────────
  decorations: {
    grain?: boolean
    radialGlows?: boolean
    organicBlobs?: boolean
    hardEdges?: boolean
    ornamentalDivider?: boolean
  }
  /** Used by the picker chip — small color swatches. */
  swatch: {
    bg: string
    ink: string
    accent: string
  }
}

// Type pairings reused across themes. Distinctive choices — no Inter-
// for-everything. Instrument Serif is already loaded in index.html and
// is the canonical Bhookly display face. Where a theme calls for
// something different the variable resolves through the standard font
// fallback chain so missing webfonts degrade gracefully.
const SERIF = '"Instrument Serif", "Iowan Old Style", "Apple Garamond", Georgia, serif'
const SANS = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif'
const DISPLAY_BOLD = '"Inter", system-ui, sans-serif'

export const POSTER_THEMES: PosterTheme[] = [
  {
    id: 'warm-saffron',
    name: 'Warm Saffron',
    tagline: 'Cream + ember — the house signature.',
    bg: 'radial-gradient(120% 80% at 50% 0%, #fef3e2 0%, #fdf8f1 55%, #f7eedd 100%)',
    ink: '#1a1410',
    inkMuted: 'rgba(26,20,16,0.62)',
    accent: '#ea580c',
    surface: '#ffffff',
    surfaceInk: '#1a1410',
    border: 'rgba(26,20,16,0.10)',
    serifFont: SERIF,
    sansFont: SANS,
    headlineLeading: 0.95,
    decorations: { grain: true, radialGlows: true },
    swatch: { bg: '#fdf8f1', ink: '#1a1410', accent: '#ea580c' },
  },
  {
    id: 'midnight-ember',
    name: 'Midnight Ember',
    tagline: 'Deep ink with saffron + rose glow.',
    bg: 'radial-gradient(60% 60% at 18% 12%, rgba(245,158,11,0.28), transparent 70%), radial-gradient(50% 60% at 82% 86%, rgba(225,29,72,0.22), transparent 70%), linear-gradient(180deg, #1a0d05 0%, #110803 100%)',
    ink: '#fdf8f1',
    inkMuted: 'rgba(253,248,241,0.62)',
    accent: '#fbbf24',
    surface: '#fdf8f1',
    surfaceInk: '#1a0d05',
    border: 'rgba(253,248,241,0.14)',
    serifFont: SERIF,
    sansFont: SANS,
    headlineLeading: 0.96,
    decorations: { grain: true, radialGlows: true },
    swatch: { bg: '#1a0d05', ink: '#fdf8f1', accent: '#fbbf24' },
  },
  {
    id: 'cream-minimal',
    name: 'Cream Minimal',
    tagline: 'All the type. None of the noise.',
    bg: '#fdf8f1',
    ink: '#1a1410',
    inkMuted: 'rgba(26,20,16,0.55)',
    accent: '#1a1410',
    surface: '#ffffff',
    surfaceInk: '#1a1410',
    border: 'rgba(26,20,16,0.12)',
    serifFont: SERIF,
    sansFont: SANS,
    headlineLeading: 0.93,
    decorations: {},
    swatch: { bg: '#fdf8f1', ink: '#1a1410', accent: '#1a1410' },
  },
  {
    id: 'brutalist-bold',
    name: 'Brutalist Bold',
    tagline: 'Hard edges. Loud type. Zero pity.',
    bg: '#0a0a0a',
    ink: '#fdf8f1',
    inkMuted: 'rgba(253,248,241,0.55)',
    accent: '#fef08a',
    surface: '#fdf8f1',
    surfaceInk: '#0a0a0a',
    border: '#fdf8f1',
    serifFont: SERIF,
    sansFont: DISPLAY_BOLD,
    headlineLeading: 0.92,
    decorations: { hardEdges: true },
    swatch: { bg: '#0a0a0a', ink: '#fdf8f1', accent: '#fef08a' },
  },
  {
    id: 'editorial-serif',
    name: 'Editorial Serif',
    tagline: 'Magazine ivory. Generous air.',
    bg: '#f7f1e3',
    ink: '#1a1410',
    inkMuted: 'rgba(26,20,16,0.55)',
    accent: '#8b3a1a',
    surface: '#ffffff',
    surfaceInk: '#1a1410',
    border: 'rgba(26,20,16,0.14)',
    serifFont: SERIF,
    sansFont: SANS,
    headlineLeading: 0.94,
    decorations: { ornamentalDivider: true },
    swatch: { bg: '#f7f1e3', ink: '#1a1410', accent: '#8b3a1a' },
  },
  {
    id: 'terracotta-glow',
    name: 'Terracotta Glow',
    tagline: 'Clay heat. Organic light.',
    bg: 'radial-gradient(80% 80% at 30% 20%, #f59e0b 0%, #c2410c 55%, #7c2d12 100%)',
    ink: '#fef3e2',
    inkMuted: 'rgba(254,243,226,0.7)',
    accent: '#fef08a',
    surface: '#fef3e2',
    surfaceInk: '#7c2d12',
    border: 'rgba(254,243,226,0.22)',
    serifFont: SERIF,
    sansFont: SANS,
    headlineLeading: 0.96,
    decorations: { grain: true, organicBlobs: true },
    swatch: { bg: '#c2410c', ink: '#fef3e2', accent: '#fef08a' },
  },
]

export function getPosterTheme(id: PosterThemeId): PosterTheme {
  return POSTER_THEMES.find((t) => t.id === id) ?? POSTER_THEMES[0]
}

// Physical dimensions per layout. These are baked into the rendered
// poster via inline `width`/`height` in millimetres so the browser
// `@media print { @page { size: … } }` rules can match the page size
// without any extra scaling.
export const LAYOUT_DIMS: Record<PosterLayout, { wMm: number; hMm: number; label: string }> = {
  a4: { wMm: 210, hMm: 297, label: 'A4 portrait' },
  tent: { wMm: 148, hMm: 210, label: 'A5 table tent' },
  card: { wMm: 148, hMm: 100, label: 'Counter card' },
}

// Logo width in mm per (layout × size). The xl row for A4 lands at
// 80mm — about 4× the legacy 56px hardcode — which is exactly what
// the brief asked for: a "BIG" logo people see across the room.
export const LOGO_SIZES_MM: Record<PosterLayout, Record<PosterLogoSize, number>> = {
  a4: { sm: 28, md: 42, lg: 58, xl: 80 },
  tent: { sm: 20, md: 30, lg: 42, xl: 56 },
  card: { sm: 16, md: 22, lg: 30, xl: 38 },
}

// QR code size in mm per layout. Held independent of logoSize because
// the QR has its own legibility floor (≥30mm for short-range scans,
// ≥45mm for across-the-room).
export const QR_SIZE_MM: Record<PosterLayout, number> = {
  a4: 78,
  tent: 60,
  card: 42,
}

/**
 * The poster QR's visual style, shared by the live preview (PosterCanvas) and
 * both export paths (posterExport's PNG and print window). Kept here — the
 * module all three already import — because a poster whose printed QR differs
 * from the approved preview is the expensive failure on this screen.
 *
 * Feed the result to buildBrandedQr/buildBrandedQrOptions, which decides error
 * correction: a centre logo forces level H regardless of the 'Q' below.
 */
export function posterQrStyle(surfaceInk: string, surface: string, accent: string) {
  return {
    dark_color: surfaceInk,
    light_color: surface,
    corner_dot_color: accent,
    margin_modules: 0,
    dots_type: 'rounded' as const,
    corner_square_type: 'extra-rounded' as const,
    corner_dot_type: 'dot' as const,
    error_correction: 'Q' as const,
  }
}

// Headline type size per layout. Uses CSS `clamp()` so the on-screen
// preview scales with viewport while the printed mm-anchored version
// hits exact PT sizes.
export const HEADLINE_SIZE_MM: Record<PosterLayout, number> = {
  a4: 22,
  tent: 14,
  card: 9.5,
}

export const SUBHEAD_SIZE_MM: Record<PosterLayout, number> = {
  a4: 7.5,
  tent: 5.2,
  card: 3.6,
}

export const FOOTER_SIZE_MM: Record<PosterLayout, number> = {
  a4: 4.5,
  tent: 3.2,
  card: 2.6,
}

// Outer padding per layout. Generous on A4 so the design breathes;
// tighter on the counter card where every mm matters.
export const PADDING_MM: Record<PosterLayout, number> = {
  a4: 18,
  tent: 12,
  card: 8,
}
